/* ===========================================================================
   Wishlist — account-based
   ---------------------------------------------------------------------------
   A wishlist belongs to a signed-in customer. Tapping a heart as a guest
   remembers what they wanted and sends them to sign in; when they come back
   signed in, that product is saved for them. Nothing they tapped is lost, and
   every list lives on an account, so it shows on every device.

   The account list is read and written through the App Proxy
   (/apps/ddc/wishlist). This script never sends a customer id — Shopify adds a
   signed one on the way through, so the server decides whose list it is. An id
   sent from here would be trivially forgeable.

   localStorage holds a working copy so hearts paint instantly on load, before
   the account list arrives.
   ========================================================================= */
(function () {
  "use strict";

  var KEY = "ddc_wishlist";
  var OWNER = "ddc_wishlist_owner";
  var PENDING = "ddc_wishlist_pending";
  var PROXY = "/apps/ddc/wishlist";
  var MAX = 50;
  var PENDING_TTL = 30 * 60 * 1000; // a save waiting on sign-in expires after 30 min

  var accountMode = false;
  var pushTimer = null;

  /* --- storage ----------------------------------------------------------
     Storage throws outright in some contexts (Safari private mode, embedded
     webviews, cookies blocked) rather than returning null. */

  function get(k) { try { return localStorage.getItem(k); } catch (e) { return null; } }
  function set(k, v) { try { localStorage.setItem(k, v); return true; } catch (e) { return false; } }
  function del(k) { try { localStorage.removeItem(k); } catch (e) {} }

  function read() {
    try {
      var raw = JSON.parse(get(KEY) || "[]");
      return Array.isArray(raw) ? raw.filter(function (x) { return x && x.handle; }) : [];
    } catch (e) {
      return [];
    }
  }

  function write(list) { return set(KEY, JSON.stringify(list.slice(0, MAX))); }

  function has(list, id) {
    return list.some(function (x) { return String(x.id) === String(id); });
  }

  function isCustomer() {
    return document.body && document.body.getAttribute("data-customer") === "1";
  }

  function loginUrl() {
    /* /customer_authentication/login keeps return_to through Shopify's sign-in
       (it is the route Shopify's own checkout uses). /account/login drops it
       and strands the shopper on their account page instead of the product. */
    return "/customer_authentication/login?return_to=" +
      encodeURIComponent(location.pathname + location.search);
  }

  /* --- hearts ----------------------------------------------------------- */

  /* One source of truth for every heart, so a card and the PDP button for the
     same product can never disagree. A guest always sees empty hearts: their
     saves are not real until they sign in, and a filled heart would promise a
     list they cannot see on their phone. */
  function paint() {
    var customer = isCustomer();
    var list = customer ? read() : [];
    var buttons = document.querySelectorAll("[data-wish]");
    for (var i = 0; i < buttons.length; i++) {
      var b = buttons[i];
      var on = has(list, b.getAttribute("data-wish-id"));
      b.setAttribute("aria-pressed", on ? "true" : "false");
      var text = b.querySelector("[data-wish-text]");
      if (text) {
        text.textContent = on ? "Saved to wishlist"
          : customer ? "Save to wishlist" : "Sign in to save";
      }
    }
    var counts = document.querySelectorAll("[data-wish-count]");
    for (var j = 0; j < counts.length; j++) counts[j].textContent = list.length;
    var link = document.querySelector("[data-wish-link]");
    if (link) link.hidden = list.length === 0;
  }

  function toggle(id, handle) {
    if (!isCustomer()) {
      /* Remember what they wanted, then sign in. applyPending() saves it the
         moment they land back here signed in. */
      set(PENDING, JSON.stringify({ id: String(id), handle: handle, at: Date.now() }));
      location.href = loginUrl();
      return;
    }
    var list = read();
    if (has(list, id)) {
      list = list.filter(function (x) { return String(x.id) !== String(id); });
    } else {
      list.unshift({ id: String(id), handle: handle, at: Date.now() });
    }
    if (!write(list)) return;
    paint();
    push();
  }

  document.addEventListener("click", function (e) {
    var b = e.target.closest("[data-wish]");
    if (!b) return;
    e.preventDefault();
    toggle(b.getAttribute("data-wish-id"), b.getAttribute("data-wish"));
  });

  /* Another tab changed the list. */
  window.addEventListener("storage", function (e) {
    if (e.key === KEY) {
      paint();
      renderPage();
    }
  });

  /* --- account sync ----------------------------------------------------- */

  function union(a, b) {
    var byId = {};
    a.concat(b).forEach(function (x) {
      var prev = byId[x.id];
      if (!prev || (x.at || 0) > (prev.at || 0)) byId[x.id] = x;
    });
    return Object.keys(byId).map(function (k) { return byId[k]; })
      .sort(function (x, y) { return (y.at || 0) - (x.at || 0); })
      .slice(0, MAX);
  }

  /* Debounced: tapping several hearts in a row is one request, not five. */
  function push() {
    if (!accountMode) return;
    clearTimeout(pushTimer);
    pushTimer = setTimeout(function () {
      fetch(PROXY, {
        method: "POST",
        /* The proxy refuses anything that is not JSON. That is the CSRF guard:
           a hostile page can post a plain form here from a signed-in shopper's
           browser, but cannot send application/json past the preflight. */
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        credentials: "same-origin",
        body: JSON.stringify({ items: read() }),
      }).catch(function () { /* the next change retries; the local copy is intact */ });
    }, 600);
  }

  function applyPending() {
    var raw = get(PENDING);
    if (!raw) return false;
    del(PENDING);
    var p;
    try { p = JSON.parse(raw); } catch (e) { return false; }
    if (!p || !p.id || !p.handle || Date.now() - (p.at || 0) > PENDING_TTL) return false;
    var list = read();
    if (has(list, p.id)) return false;
    list.unshift({ id: String(p.id), handle: p.handle, at: Date.now() });
    write(list);
    return true;
  }

  /* Resolves true when it changed the stored list, so the page re-renders only
     when there is something new to show. */
  function pull() {
    if (!isCustomer()) {
      /* A list left by a signed-in account is cleared once they sign out, so
         on a shared device the next person never sees it. */
      if (get(OWNER)) {
        write([]);
        del(OWNER);
        paint();
        return Promise.resolve(true);
      }
      return Promise.resolve(false);
    }

    return fetch(PROXY, { headers: { Accept: "application/json" }, credentials: "same-origin" })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        if (!data || !data.customer) return false;
        accountMode = true;
        var mergedKey = "ddc_wishlist_merged_" + data.customerKey;

        if (!get(mergedKey)) {
          /* First time this device meets this account: keep anything saved
             here before sign-in was required, plus what the account holds. */
          write(union(read(), data.items || []));
          set(mergedKey, String(Date.now()));
          push();
        } else {
          /* After that the ACCOUNT is the truth. Unioning on every load would
             resurrect anything removed on another device, forever. */
          write(data.items || []);
        }
        if (applyPending()) push();
        set(OWNER, data.customerKey);
        paint();
        return true;
      })
      .catch(function () { return false; /* offline or proxy down: keep the local copy */ });
  }

  /* --- wishlist page ---------------------------------------------------- */

  function money(paise) {
    return "₹" + (paise / 100).toLocaleString("en-IN", {
      minimumFractionDigits: paise % 100 ? 2 : 0,
      maximumFractionDigits: 2,
    });
  }

  function esc(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c];
    });
  }

  function renderPage() {
    var root = document.getElementById("WishlistPage");
    /* A guest's page is a sign-in prompt rendered in Liquid; nothing to fill. */
    if (!root || !isCustomer()) return;
    var grid = root.querySelector("[data-wish-grid]");
    var empty = root.querySelector("[data-wish-empty]");
    var list = read();

    if (!list.length) {
      grid.innerHTML = "";
      empty.hidden = false;
      return;
    }
    empty.hidden = true;
    grid.setAttribute("aria-busy", "true");

    Promise.all(list.map(function (item) {
      return fetch("/products/" + encodeURIComponent(item.handle) + ".js")
        .then(function (r) { return r.ok ? r.json() : null; })
        .catch(function () { return null; });
    })).then(function (products) {
      var alive = [];
      var html = "";
      products.forEach(function (p, i) {
        if (!p) return; // deleted, unpublished or renamed: prune it
        alive.push(list[i]);
        var v = p.variants.filter(function (x) { return x.available; })[0] || p.variants[0];
        var src = p.featured_image || "";
        if (src) src += (src.indexOf("?") > -1 ? "&" : "?") + "width=500";
        html +=
          '<li class="ddc-wl__card">' +
            '<a class="ddc-wl__media" href="/products/' + esc(p.handle) + '">' +
              (src ? '<img src="' + esc(src) + '" alt="' + esc(p.title) + '" loading="lazy" width="250" height="250">' : "") +
            "</a>" +
            '<a class="ddc-wl__name" href="/products/' + esc(p.handle) + '">' + esc(p.title) + "</a>" +
            '<p class="ddc-wl__price">' +
              (p.variants.length > 1 ? "From " : "") + money(p.price_min) +
              (p.available ? "" : " <em>&mdash; sold out</em>") +
            "</p>" +
            '<div class="ddc-wl__actions">' +
              '<button type="button" class="ddc-btn ddc-btn--cart ddc-wl__add" data-add="' + v.id + '"' +
                (p.available ? "" : " disabled") + ">" +
                (p.available ? "Add to cart" : "Sold out") +
              "</button>" +
              '<button type="button" class="ddc-wl__remove" data-wish="' + esc(p.handle) +
                '" data-wish-id="' + p.id + '" aria-pressed="true" aria-label="Remove ' +
                esc(p.title) + ' from wishlist">Remove</button>' +
            "</div>" +
          "</li>";
      });
      if (alive.length !== list.length) {
        write(alive);
        /* Tell the account too, or the dead entry comes back on the next load
           and is pruned again, forever. */
        push();
      }
      grid.innerHTML = html;
      grid.removeAttribute("aria-busy");
      empty.hidden = alive.length > 0;
      paint();
    });
  }

  document.addEventListener("click", function (e) {
    var add = e.target.closest("[data-add]");
    if (add) {
      add.disabled = true;
      /* /cart/add.js leaves cart attributes untouched, so an affiliate click
         recorded earlier still reaches the order. */
      fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ id: Number(add.getAttribute("data-add")), quantity: 1 }),
      }).then(function (r) {
        add.textContent = r.ok ? "Added" : "Try again";
        if (!r.ok) add.disabled = false;
      }).catch(function () {
        add.textContent = "Try again";
        add.disabled = false;
      });
      return;
    }
    if (e.target.closest(".ddc-wl__remove")) setTimeout(renderPage, 0);
  });

  function init() {
    paint();
    renderPage();
    pull().then(function (changed) { if (changed) renderPage(); });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
